import { computeEstimatePricingPreview } from './estimate-pricing-preview';
import { offerCountedLines, splitOptionalOffers } from './service-components';
import { isMembershipSetupFeeLine } from '@notifi/shared-types/membership-line';
import {
  membershipOfferForBillingOption,
  membershipOfferFromSnapshot,
  membershipOfferPriceIntervalLabel,
} from '@/lib/sales/membership-offer';
import {
  getProposalOptionDepositEligibleTotal,
  proposalOptionDepositAmountOn,
} from '@/components/organisms/proposals/support/proposal-option-math';
import {
  proposalOptionBreakdown,
  proposalSelectedOptionalRows,
  splitProposalLines,
} from '@/lib/sales/proposalOptionBreakdown';
import type { EstimateMembershipOffer } from '@/types/estimate';
import type { ProposalLineItem, ProposalOption, ProposalOptionAttachment } from '@/types/proposal';

/** The files one tab shows: the proposal-wide attachments PLUS the active
 *  option's own, deduped by id — so option-specific files appear under their own
 *  tab and never leak across options or vanish. */
export function mergeOptionAttachments(
  proposalLevel: ProposalOptionAttachment[] | undefined,
  optionLevel: ProposalOptionAttachment[] | undefined,
): ProposalOptionAttachment[] {
  const base = proposalLevel ?? [];
  const extra = (optionLevel ?? []).filter((a) => !base.some((b) => b.id === a.id));
  return [...base, ...extra];
}

/** A line's money contribution to the option total. `hidePrice` hides the
 *  per-line figure in the itemized breakdown, NOT the money — the summary
 *  subtotal/total still include it (mirroring the estimate), or the rows would
 *  not reconcile to the Total the customer approves. */
function lineAmount(line: ProposalLineItem): number {
  return line.lineListTotal ?? line.lineTotal ?? 0;
}

/** A hide-price line's figure must never surface as its own row; its money is
 *  folded into the aggregate "Scope of work" line instead. */
function isHiddenPrice(line: ProposalLineItem): boolean {
  return line.displayConfig?.hidePrice === true;
}

/**
 * The join-&-save membership helpers shared by every single-scope proposal
 * surface (public page + customer portal), so the price a member sees when they
 * tick the offer, and the deposit charged on the eligible base, are computed
 * identically everywhere. Mirrors the classic proposal page's math.
 */

/** The offer for one option at a chosen billing cadence (null when the option
 *  carries no priced offer). */
export function proposalMembershipOffer(
  option: ProposalOption,
  billingOptionId?: string | null,
): EstimateMembershipOffer | null {
  const offer = membershipOfferFromSnapshot(option.membershipPlanSnapshot);
  const selectedOffer = membershipOfferForBillingOption(offer, billingOptionId);
  return selectedOffer?.planPrice == null ? null : selectedOffer;
}

/** The headline price the option quotes — member-repriced work plus the plan
 *  price when the offer is ticked, otherwise the stored option total. */
export function proposalOptionDisplayTotal(
  option: ProposalOption,
  membershipSelected: boolean,
  billingOptionId?: string | null,
): number {
  if (option.selectionPricing) return option.selectionPricing.total;
  const offer = proposalMembershipOffer(option, billingOptionId);
  return membershipSelected && offer?.planPrice != null
    ? Math.round(
        (offer.memberGrandTotal + offer.planPrice + (offer.setupFee ?? 0) + Number.EPSILON) * 100,
      ) / 100
    : option.total;
}

/** The deposit due — charged on the deposit-ELIGIBLE base (membership plan lines
 *  excluded; when the offer is ticked the eligible work reprices to member rates,
 *  which is `memberGrandTotal`), so the plan price never inflates the deposit. */
export function proposalOptionDepositDue(
  option: ProposalOption,
  membershipSelected: boolean,
  billingOptionId?: string | null,
): number {
  if (option.selectionPricing) return option.selectionPricing.depositDue;
  const offer = membershipSelected ? proposalMembershipOffer(option, billingOptionId) : null;
  const base =
    offer?.planPrice != null
      ? Math.max(0, Math.round((offer.memberGrandTotal + Number.EPSILON) * 100) / 100)
      : getProposalOptionDepositEligibleTotal(option);
  return proposalOptionDepositAmountOn(option, base);
}

/**
 * The deposit the customer must pay BEFORE this option can be approved — the
 * option's deposit when it is marked "Required to approve", otherwise 0. An
 * option with such a deposit is not accepted on the proposal: it is chosen,
 * the estimate is born SENT, and the deposit is paid and the estimate approved
 * there, exactly as a required-deposit estimate works.
 */
export function proposalOptionDepositBeforeApproval(
  option: ProposalOption,
  membershipSelected: boolean,
  billingOptionId?: string | null,
): number {
  if (option.depositRequired !== true) return 0;
  return proposalOptionDepositDue(option, membershipSelected, billingOptionId);
}

/**
 * The membership savings shown in the breakdown. The option's stored savings
 * are what the customer already gets through the plan(s) they hold (the lines
 * are priced with them); ticking the offer ADDS what joining the offered plan
 * saves on top — the offer's `savings` is that increment, not a replacement.
 * A non-member's stored savings are 0, so this is the offer's alone.
 */
export function proposalOptionMembershipSavings(
  membershipSelected: boolean,
  offer: EstimateMembershipOffer | null,
  storedSavings: number,
): number {
  if (!membershipSelected) return storedSavings;
  return Math.round((storedSavings + (offer?.savings ?? 0) + Number.EPSILON) * 100) / 100;
}

/** Use the frozen pre-document member prices for the two distinct discounts.
 * Offer savings compares final totals and therefore also includes tax and the
 * change in document discount; it is not the canonical membership discount. */
export function proposalOptionSelectedBreakdown(
  option: ProposalOption,
  membershipSelected: boolean,
  offer: EstimateMembershipOffer | null,
) {
  const bd = proposalOptionBreakdown(option);
  // This response belongs to the exact current selection. Do not reconstruct
  // discounts/taxes from the customer projection, which may omit billed work.
  if (option.selectionPricing) return { ...bd, ...option.selectionPricing };
  const fallback = {
    ...bd,
    membershipSavings: proposalOptionMembershipSavings(
      membershipSelected,
      offer,
      bd.membershipSavings,
    ),
  };
  const context = membershipSelected ? offer?.selectionPricing : null;
  if (!context || !offer) return fallback;
  const allLines = option.lineItems ?? [];
  const split = splitOptionalOffers(allLines);
  const sold = new Set([
    ...split.required,
    ...split.offers.flatMap((group) => offerCountedLines(group, group.parent.isSelected !== false)),
  ]);
  const selected = context.lines.map((row) => {
    const source = row.lineKey
      ? allLines.find((line) => line.lineKey === row.lineKey)
      : allLines[row.lineIndex];
    // Public projections may omit hidden work, but its frozen price remains.
    return source ? sold.has(source) : row.isSelected;
  });
  const preview = computeEstimatePricingPreview({
    lineItems: context.lines.map((row, index) => ({
      ...row,
      _rowId: String(row.lineIndex),
      quantity: 1,
      basePrice: row.memberNet,
      laborRate: 0,
      costBasis: 0,
      markupPercent: 0,
      priceStrategy: 'FLAT',
      isSelected: selected[index]!,
    })),
    taxResolution: null,
    pricingMode: 'STANDARD',
    membershipInfo: null,
    discountType: context.documentDiscount.type === 'PERCENT' ? '%' : '$',
    discountValue: context.documentDiscount.value,
  });
  const round = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
  return {
    ...bd,
    discount: preview.discountAmount,
    membershipSavings: round(
      bd.membershipSavings +
        context.lines.reduce(
          (sum, row, index) => sum + (selected[index] ? row.originalNet - row.memberNet : 0),
          0,
        ),
    ),
    // Tax is frozen in the offered grand total; do not use today's tax config.
    tax: round(offer.memberGrandTotal - preview.grandTotal),
  };
}

/**
 * Names the plans the savings come from: the held plan, the offered plan once
 * it is ticked, or both ("Gold + Platinum") — so the row never credits the
 * offered plan with savings the customer's own plan is giving them.
 */
export function proposalOptionMembershipSavingsPlanNames(
  option: ProposalOption,
  membershipSelected: boolean,
  offer: EstimateMembershipOffer | null,
  storedSavings: number,
): string {
  const names: string[] = [];
  const held = (option.membershipPlanSnapshot as { planName?: string | null } | null | undefined)
    ?.planName;
  if (storedSavings > 0) {
    // The public projection strips the member-priced snapshot down to its
    // offer, so the held plan is often nameless here. Crediting the OFFERED
    // plan with savings the held plan gives would be wrong — say nothing.
    if (!held) return '';
    names.push(held);
  }
  if (membershipSelected && offer?.planName) names.push(offer.planName);
  return names.join(' + ');
}

/** The totals recap the approve modal prints for one option at its membership
 *  choice — the same rows the estimate's approve sheet shows. */
export function proposalApproveRecap(
  option: ProposalOption,
  membershipSelected: boolean,
  billingOptionId: string | null,
  optionLabel: string,
) {
  const { required } = splitProposalLines(option);
  const offer = proposalMembershipOffer(option, billingOptionId);
  const bd = proposalOptionSelectedBreakdown(option, membershipSelected, offer);
  const requiredSetupFee = required
    .filter((line) => isMembershipSetupFeeLine(line) && !isHiddenPrice(line))
    .reduce((sum, line) => sum + lineAmount(line), 0);
  const optionalRows = proposalSelectedOptionalRows(option);
  // Public projections omit hidden billable lines. Keep their amounts in the
  // aggregate scope using the stored subtotal, without exposing hidden details
  // or counting separately displayed optionals twice.
  const scopeAmount =
    Math.round(
      (bd.subtotal - requiredSetupFee - optionalRows.reduce((sum, row) => sum + row.subtotal, 0)) *
        100,
    ) / 100;
  return {
    scopeLabel: `Scope of work${optionLabel ? ` — ${optionLabel}` : ''}`,
    requiredSubtotal: scopeAmount,
    optionalRows,
    membershipSavings: bd.membershipSavings,
    membershipSavingsLabel: proposalOptionMembershipSavingsPlanNames(
      option,
      membershipSelected,
      offer,
      proposalOptionBreakdown(option).membershipSavings,
    ),
    membershipPlanName: offer?.planName ?? null,
    membershipPlanPrice: membershipSelected ? (offer?.planPrice ?? null) : null,
    membershipPlanInterval: membershipSelected ? membershipOfferPriceIntervalLabel(offer) : null,
    membershipSetupFee: requiredSetupFee + (membershipSelected ? (offer?.setupFee ?? 0) : 0),
    discount: bd.discount,
    tax: bd.tax,
    total: proposalOptionDisplayTotal(option, membershipSelected, billingOptionId),
    depositDue: proposalOptionDepositDue(option, membershipSelected, billingOptionId),
  };
}
